import { useCallback, useEffect, useState } from 'react'
import { useTickerApi } from './useTickerApi'
import StatsSource from './StatsSource'
import TickerRows from './TickerRows'
import { LEADERS_PRESETS, LEADERS_TOKENS, leadersContext, renderTemplate } from './tickerFormat'

const METRICS = [
  { id: 'goals', label: 'Goals' },
  { id: 'assists', label: 'Assists' },
  { id: 'points', label: 'Points' },
]

// Scoring leaders per division, from the league's own stats listing rather than
// our stored box scores — the league counts games we never broadcast, so its
// totals are the ones that will match what a viewer sees elsewhere.
export default function Leaders() {
  const api = useTickerApi()
  const [seasonId, setSeasonId] = useState('')
  const [metrics, setMetrics] = useState(['goals', 'assists', 'points'])
  const [top, setTop] = useState(6)
  const [groups, setGroups] = useState([])
  const [league, setLeague] = useState('')

  const load = useCallback(() => {
    if (!seasonId || metrics.length === 0) { setGroups([]); return }
    Promise.all(metrics.map(m =>
      api.listLeaders(seasonId, { metric: m, top })
        .then(r => (r?.groups || []).map(g => ({ ...g, metric: m })))
        .catch(() => [])
    )).then(lists => setGroups(lists.flat()))
    api.listSeasons().then(r => {
      const s = (r?.seasons || []).find(x => String(x.id) === String(seasonId))
      setLeague(s?.league || '')
    }).catch(() => {})
  }, [api, seasonId, metrics, top])

  useEffect(() => { load() }, [load])

  const toggleMetric = (id) => setMetrics(prev =>
    prev.includes(id) ? prev.filter(m => m !== id) : [...METRICS.map(m => m.id)].filter(m => prev.includes(m) || m === id))

  return (
    <div>
      <StatsSource seasonId={seasonId} setSeasonId={setSeasonId} onRefreshed={load}>
        <div style={{ display: 'grid', gap: 4 }}>
          <span className="muted" style={{ fontSize: 12 }}>Metrics</span>
          <div style={{ display: 'flex', gap: 6 }}>
            {METRICS.map(m => (
              <button key={m.id} onClick={() => toggleMetric(m.id)}
                style={{
                  fontSize: 12, padding: '3px 10px', borderRadius: 12,
                  border: '1px solid var(--border-color, #2a2a2a)',
                  background: metrics.includes(m.id) ? 'var(--accent, #4a90e2)' : 'transparent',
                  color: metrics.includes(m.id) ? '#fff' : 'var(--text-muted, #888)',
                  cursor: 'pointer',
                }}>{m.label}</button>
            ))}
          </div>
        </div>
        <label style={{ display: 'grid', gap: 4 }}>
          <span className="muted" style={{ fontSize: 12 }}>Players per line</span>
          <input type="number" min="1" max="20" value={top} style={{ width: 70 }}
            onChange={e => setTop(Math.max(1, Number(e.target.value) || 6))} />
        </label>
      </StatsSource>

      <TickerRows
        storageKey="tbtv.ticker.leaders.settings"
        presets={LEADERS_PRESETS}
        tokens={LEADERS_TOKENS}
        rows={groups}
        renderItem={(g, template) => renderTemplate(template, leadersContext(g, { league, metric: g.metric }))}
        rowLabel={(g) => `${g.division_group} ${g.metric}`}
        emptyMessage="No skater stats stored yet. Use Refresh from league above."
      />
    </div>
  )
}
